/**
 * The map's lifecycle on the card — two hooks over one cache (`stampTile.ts#loadTile`).
 *
 * `useStampTile` — the THUMBNAIL: fetches the be's tile ONLY when the fix moves to a different z16 tile (a walk inside
 * one tile costs one request); while a shot is in flight the caller passes the frozen fix, so the key is unchanged and
 * nothing refetches. `useStampMosaic` — the EXPANDED sheet: the 3×3 ring around that tile, nine cached requests,
 * fetched only while the sheet is open. Every `<img>` src is an object URL from the fetched blob (the bearer cannot
 * ride an `<img>` request), revoked when replaced and on close / unmount. Errors never block the shot: the view says
 * `error` and the card prints one small note; OFF / HIDDEN / an older be say `none` and the card prints nothing.
 */

import { useEffect, useRef, useState } from 'react';
import { type EdgeSide, type TilePin, type TileRef, edgeSideFor, loadTile, mosaicRefs, pinWithin, pinWithinMosaic, tileFor, tileKey } from './stampTile';

export interface TileView {
  readonly status: 'none' | 'loading' | 'ready' | 'error';
  /** An object URL for the tile PNG while `ready`. */
  readonly url: string | null;
  readonly ref: TileRef | null;
  /** The fix's own marker inside the tile. */
  readonly pin: TilePin | null;
  /** The registered anchor's marker — only when it falls inside the same tile. */
  readonly anchorPin: TilePin | null;
  readonly reason: 'unavailable' | 'rate-limited' | 'network' | 'unexpected' | null;
}

const NONE: TileView = { status: 'none', url: null, ref: null, pin: null, anchorPin: null, reason: null };

interface Loaded {
  readonly key: string;
  readonly status: 'loading' | 'ready' | 'error';
  readonly url: string | null;
  readonly reason: TileView['reason'];
}

type LatLng = { readonly lat: number; readonly lng: number };

export function useStampTile(fix: LatLng | null, enabled: boolean, anchor: LatLng | null): TileView {
  const target = fix && enabled ? tileFor(fix.lat, fix.lng) : null;
  const key = target ? tileKey(target.ref) : null;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const urlRef = useRef<string | null>(null);
  const seq = useRef(0);

  // the tile KEY is the trigger: the same tile ⇒ the same request, however the fix moves inside it
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the tile, not the fix (a walk inside one tile costs one request)
  useEffect(() => {
    const revoke = (): void => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
    if (!key || !target) {
      seq.current += 1;
      revoke();
      setLoaded(null);
      return undefined;
    }
    const mine = ++seq.current;
    setLoaded({ key, status: 'loading', url: null, reason: null });
    void loadTile(target.ref).then((answer) => {
      if (mine !== seq.current) return; // the fix moved to another tile (or the view is gone) while this was in flight
      revoke();
      if (answer.kind === 'png') {
        const url = URL.createObjectURL(answer.blob);
        urlRef.current = url;
        setLoaded({ key, status: 'ready', url, reason: null });
      } else if (answer.kind === 'none') {
        setLoaded(null);
      } else {
        setLoaded({ key, status: 'error', url: null, reason: answer.reason });
      }
    });
    return () => {
      seq.current += 1;
      revoke();
    };
  }, [key]);

  if (!target || !key || !loaded || loaded.key !== key) return NONE;
  return {
    status: loaded.status,
    url: loaded.url,
    ref: target.ref,
    pin: target.pin,
    anchorPin: anchor ? pinWithin(target.ref, anchor.lat, anchor.lng) : null,
    reason: loaded.reason,
  };
}

export interface MosaicTile {
  readonly ref: TileRef;
  /** An object URL while the tile answered a PNG; null = a gap (OFF, error, or still loading). */
  readonly url: string | null;
}

export interface MosaicView {
  /** `closed` while not open; `loading` until the centre answers; `ready` when it drew; `error` when it could not. */
  readonly status: 'closed' | 'loading' | 'ready' | 'error';
  readonly centre: TileRef | null;
  /** Nine cells, row-major from the north-west (index 4 = the centre). */
  readonly tiles: readonly MosaicTile[];
  /** The registered anchor inside the mosaic (pixels of 768), or null … */
  readonly anchorPin: TilePin | null;
  /** … in which case the side it lies on, for the edge arrow; null when there is no anchor. */
  readonly edge: EdgeSide | null;
  readonly reason: TileView['reason'];
}

const CLOSED: MosaicView = { status: 'closed', centre: null, tiles: [], anchorPin: null, edge: null, reason: null };

export function useStampMosaic(centre: TileRef | null, anchor: LatLng | null): MosaicView {
  const key = centre ? tileKey(centre) : null;
  const [state, setState] = useState<{ key: string; status: MosaicView['status']; urls: (string | null)[]; reason: TileView['reason'] } | null>(null);
  const urlsRef = useRef<string[]>([]);
  const seq = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the centre tile (nine cached requests per centre, not per render)
  useEffect(() => {
    const revokeAll = (): void => {
      for (const u of urlsRef.current) URL.revokeObjectURL(u);
      urlsRef.current = [];
    };
    if (!key || !centre) {
      seq.current += 1;
      revokeAll();
      setState(null);
      return undefined;
    }
    const mine = ++seq.current;
    const refs = mosaicRefs(centre);
    setState({ key, status: 'loading', urls: refs.map(() => null), reason: null });
    void Promise.all(refs.map((r) => loadTile(r))).then((answers) => {
      if (mine !== seq.current) return;
      revokeAll();
      const urls = answers.map((a) => (a.kind === 'png' ? URL.createObjectURL(a.blob) : null));
      urlsRef.current = urls.filter((u): u is string => u !== null);
      const middle = answers[Math.floor(answers.length / 2)];
      if (middle?.kind === 'png') setState({ key, status: 'ready', urls, reason: null });
      else if (middle?.kind === 'error') setState({ key, status: 'error', urls, reason: middle.reason });
      else setState({ key, status: 'ready', urls, reason: null }); // a centre 204 cannot open a sheet; neighbours may still be gaps
    });
    return () => {
      seq.current += 1;
      revokeAll();
    };
  }, [key]);

  if (!centre || !key || !state || state.key !== key) return CLOSED;
  const refs = mosaicRefs(centre);
  return {
    status: state.status,
    centre,
    tiles: refs.map((ref, i) => ({ ref, url: state.urls[i] ?? null })),
    anchorPin: anchor ? pinWithinMosaic(centre, anchor.lat, anchor.lng) : null,
    edge: anchor && !pinWithinMosaic(centre, anchor.lat, anchor.lng) ? edgeSideFor(centre, anchor.lat, anchor.lng) : null,
    reason: state.reason,
  };
}
